import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { useAuth } from '../../auth/AuthContext';
import { useWallet } from '../../context/WalletContext';
import useRequireAuth from '../../hooks/useRequireAuth';
import { canShowBackfillInviter } from '../../utils/memberInviter';

const BASE_MENU_ITEMS = [
  { label: '資金', path: '/wallet', auth: true },
  { label: '身份認證', path: '/kyc', auth: true },
  { label: '安全中心', path: '/security', auth: true },
  { label: '提款方式', path: '/withdraw/methods', auth: true },
  { label: '訊息公告', path: '/messages', auth: false },
  { label: '幫助中心', path: '/help', auth: false },
  { label: '客服中心', path: '/support', auth: false },
  { label: '通用設定', path: '/settings', auth: false },
];

const BACKFILL_INVITER_MENU_ITEM = {
  label: '輸入邀請碼',
  path: '/backfill-inviter',
  auth: true,
};

export default function UserDrawer({ open, onClose }) {
  const { isAuthenticated, user, logout } = useAuth();
  const { balance } = useWallet();
  const requireAuth = useRequireAuth();
  const [memberInfo, setMemberInfo] = useState(null);

  useEffect(() => {
    if (!open || !isAuthenticated) {
      setMemberInfo(null);
      return undefined;
    }

    let cancelled = false;
    memberApi
      .getInfo()
      .then((info) => {
        if (!cancelled) {
          setMemberInfo(info || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMemberInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, isAuthenticated]);

  const menuItems = useMemo(() => {
    if (!canShowBackfillInviter(memberInfo)) {
      return BASE_MENU_ITEMS;
    }
    const items = [...BASE_MENU_ITEMS];
    const insertAt = items.findIndex((item) => item.path === '/withdraw/methods') + 1;
    items.splice(insertAt, 0, BACKFILL_INVITER_MENU_ITEM);
    return items;
  }, [memberInfo]);

  if (!open) {
    return null;
  }

  function handleMenuClick(item) {
    onClose();
    if (item.auth) {
      requireAuth(item.path);
      return;
    }
  }

  return (
    <div className="user-drawer">
      <button type="button" className="user-drawer__backdrop" aria-label="關閉" onClick={onClose} />
      <aside className="user-drawer__panel">
        <div className="user-drawer__profile">
          <div className="user-drawer__avatar" aria-hidden>
            {(user?.nickname || user?.username || 'G').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="user-drawer__uid">
              UID：{user?.id || '—'}
            </div>
            <div className="user-drawer__score">信譽分數：100</div>
          </div>
        </div>

        <div className="user-drawer__asset">
          <div className="user-drawer__asset-label">USDT 資產明細</div>
          <div className="user-drawer__asset-value">
            {isAuthenticated ? balance ?? '—' : '—'}
          </div>
          <div className="user-drawer__asset-actions">
            <button type="button" className="user-drawer__gold-btn" onClick={() => { onClose(); requireAuth('/deposit'); }}>
              充幣
            </button>
            <button type="button" className="user-drawer__gold-btn" onClick={() => { onClose(); requireAuth('/withdraw'); }}>
              提幣
            </button>
          </div>
        </div>

        <ul className="user-drawer__menu">
          {menuItems.map((item) => (
            <li key={item.label}>
              {item.auth ? (
                <button type="button" className="user-drawer__menu-item" onClick={() => handleMenuClick(item)}>
                  <span>{item.label}</span>
                  <Chevron />
                </button>
              ) : (
                <Link to={item.path} className="user-drawer__menu-item" onClick={onClose}>
                  <span>{item.label}</span>
                  <Chevron />
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="user-drawer__footer">
          {isAuthenticated ? (
            <button type="button" className="user-drawer__logout" onClick={() => { logout(); onClose(); }}>
              登出
            </button>
          ) : (
            <div className="user-drawer__auth-links">
              <Link to="/login" className="user-drawer__gold-btn" onClick={onClose}>登入</Link>
              <Link to="/register" className="user-drawer__outline-btn" onClick={onClose}>註冊</Link>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
